// components/FileDropzone.tsx
import { UploadCloud } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

export default function FileDropzone({
  onFileSelected,
  accept = "pdf,doc,docx,xls,xlsx,jpg,jpeg,png",
  selectedFiles,
  onInvalidFiles,
}: {
  onFileSelected: (files: File[]) => void;
  accept?: string;
  selectedFiles?: File[];
  onInvalidFiles?: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileNames, setFileNames] = useState<string[]>([]);

  useEffect(() => {
    if (selectedFiles) {
      setFileNames(selectedFiles.map((file) => file.name));
    }
  }, [selectedFiles]);

  const handleFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const allowedExtensions = accept
      .replace(/\s/g, "")
      .split(",")
      .map((extension: string) => extension.replace(/^\./, "").toLowerCase())
      .filter(Boolean);
    const validFiles = fileArray.filter((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
      return allowedExtensions.includes(extension);
    });
    const invalidFiles = fileArray.filter((file) => !validFiles.includes(file));

    if (invalidFiles.length > 0) {
      onInvalidFiles?.(invalidFiles);
    }

    setFileNames(validFiles.map((f) => f.name));
    onFileSelected(validFiles);
  };
  
  return (
    <div className="w-full">
      <label className="text-sm font-medium text-gray-700 block mb-1">
        Fichier(s)
      </label>
      <div
        className={clsx(
          "flex flex-col items-center justify-center border-2 border-dashed rounded-md p-6 cursor-pointer transition-all text-center",
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white"
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (e.dataTransfer.files?.length > 0) {
            handleFiles(e.dataTransfer.files);
          }
        }}
      >
        <UploadCloud className="w-6 h-6 text-gray-500 mb-2" />
        <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
          <span className="font-semibold">Cliquez pour ajouter</span> ou
          glissez-déposez
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Types autorisés : {accept}
        </p>

        {fileNames.length > 0 && (
          <ul className="mt-3 text-xs text-gray-700 font-medium space-y-1">
            {fileNames.map((name, idx) => (
              <li key={idx}>• {name}</li>
            ))}
          </ul>
        )}

        <input
          type="file"
          accept={accept.replace(/\s/g, "").split(",").map((a: string) => "." + a.toLowerCase()).join(",")}
          multiple
          ref={inputRef}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) {
              handleFiles(e.target.files);
            }
          }}
        />
      </div>
    </div>
  );
}
